import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import { getSyncProgressState } from '~/data/sync/syncProgressState';
import { useSyncProgress } from '~/data/sync/useSyncProgress';

import classes from './style.module.scss';
import { SyncProgressItem, SyncProgressStatus } from '../syncProgressTypes';

const SUCCESS_AUTO_HIDE_MS = 2000;
const HEIGHT_ANIMATION_MS = 150;

function countFailed(items: SyncProgressItem[]): number {
  return items.filter((item) => item.status === 'failed').length;
}

export type SyncStatusChipProps = {
  /** Set when the user chose "Continue in background" on SyncProgressDialog. */
  visible: boolean;
  onOpenDialog: () => void;
  /** Called after the success message auto-hides so the parent can clear background mode. */
  onAutoHide?: () => void;
  className?: string;
  /** When the chip is the first row under the status bar (not below AppHeader). */
  atScreenTop?: boolean;
};

export type SyncStatusChipComponent = React.FC<SyncStatusChipProps>;

const SyncStatusChip: SyncStatusChipComponent = ({
  visible,
  onOpenDialog,
  onAutoHide,
  className,
  atScreenTop = false,
}) => {
  const { t } = useTranslation('offline.syncProgress');
  const { status, total, completed, items } = useSyncProgress();
  const [reduceMotion, setReduceMotion] = useState(false);
  const [mounted, setMounted] = useState(false);

  const failedCount = useMemo(() => countFailed(items), [items]);

  const stripVisible = visible && status !== 'idle';

  const message = useMemo(() => {
    switch (status) {
      case 'syncing':
        return total === 1
          ? t('stripSyncing_one')
          : t('stripSyncing_other', { completed, total });
      case 'paused':
        return t('stripPaused');
      case 'success':
        return t('titleSuccess');
      case 'partial_failure':
        return t(
          failedCount === 1 ? 'chipNeedsAttention_one' : 'chipNeedsAttention_other',
          { count: failedCount }
        );
      default:
        return '';
    }
  }, [completed, failedCount, status, t, total]);

  const ariaLiveMessage = useMemo(() => {
    switch (status) {
      case 'syncing':
        return t('ariaLiveSyncing', { completed, total });
      case 'paused':
        return t('ariaLivePaused');
      case 'success':
        return t('ariaLiveSuccess');
      case 'partial_failure':
        return t(
          failedCount === 1 ? 'ariaLiveNeedsAttention_one' : 'ariaLiveNeedsAttention_other',
          { count: failedCount }
        );
      default:
        return '';
    }
  }, [completed, failedCount, status, t, total]);

  const actionLabel = useMemo(() => {
    switch (status) {
      case 'syncing':
        return t('stripActionDetails');
      case 'partial_failure':
        return t('stripActionRetry');
      default:
        return null;
    }
  }, [status, t]);

  const progressRatio = useMemo(() => {
    if (status !== 'syncing' || total <= 0) {
      return 0;
    }
    return Math.min(1, Math.max(0, completed / total));
  }, [completed, status, total]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!visible || status !== 'success') {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (getSyncProgressState().status === 'success') {
        onAutoHide?.();
      }
    }, SUCCESS_AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [onAutoHide, status, visible]);

  const handleClick = useCallback(() => {
    onOpenDialog();
  }, [onOpenDialog]);

  const renderIcon = (chipStatus: SyncProgressStatus): React.ReactNode => {
    switch (chipStatus) {
      case 'syncing':
        return (
          <Icon
            name="sync"
            size="20"
            className={classes.spinIcon}
            aria-hidden
          />
        );
      case 'paused':
        return <Icon name="cloud_off" size="20" aria-hidden />;
      case 'success':
        return <Icon name="check_circle" size="20" aria-hidden />;
      case 'partial_failure':
        return <Icon name="warning" size="20" aria-hidden />;
      default:
        return null;
    }
  };

  const buttonAriaLabel = actionLabel
    ? `${t('chipOpenSync')}. ${message}. ${actionLabel}`
    : `${t('chipOpenSync')}. ${message}`;

  const show = stripVisible && Boolean(message);

  useEffect(() => {
    if (show) {
      setMounted(true);
      return undefined;
    }

    if (reduceMotion) {
      setMounted(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setMounted(false), HEIGHT_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [reduceMotion, show]);

  if (!mounted) {
    return null;
  }

  return (
    <div
      className={classNames(
        classes.collapse,
        show && classes.collapseOpen,
        reduceMotion && classes.collapseReducedMotion,
        atScreenTop && classes.rootAtScreenTop,
        className
      )}
      style={{ transitionDuration: `${reduceMotion ? 0 : HEIGHT_ANIMATION_MS}ms` }}
    >
      <div className={classes.collapseInner}>
        <p className={classes.srOnly} aria-live="polite" aria-atomic="true">
          {ariaLiveMessage}
        </p>
        <button
          type="button"
          className={classNames(classes.strip, classes[`strip--${status}`])}
          onClick={handleClick}
          aria-label={buttonAriaLabel}
        >
          <span className={classes.leadingIcon} aria-hidden>
            {renderIcon(status)}
          </span>
          <span className={classes.message}>{message}</span>
          {actionLabel && (
            <span className={classes.action} aria-hidden>
              <span className={classes.actionLabel}>{actionLabel}</span>
              <Icon name="keyboard_arrow_right" size="20" />
            </span>
          )}
          {status === 'syncing' && (
            <span
              className={classes.progressLine}
              style={{ width: `${progressRatio * 100}%` }}
              aria-hidden
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default SyncStatusChip;
